import {usePrevious} from '@wandb/weave/hookUtils';
import {useCallback, useEffect, useRef, useState} from 'react';
import {type FollowOutputScalarType} from 'react-virtuoso';

import {
  JUMP_TO_BOTTOM_DISTANCE_SHOW_PX,
  JUMP_TO_BOTTOM_MAX_SINGLE_SCROLL_PX,
  JUMP_TO_BOTTOM_THRESHOLD_PX,
} from '../constants';

export interface UseAutoScrollArgs {
  /** Virtuoso's `firstItemIndex`; used to distinguish prepends from appends. */
  firstItemIndex: number;
  /** Whether the list mounts at a specific non-bottom position — a restored
   * scroll snapshot or a seeded search match. When true, auto-scroll starts
   * unpinned and re-pins only if Virtuoso reports the position is at the bottom. */
  startsUnpinned?: boolean;
  /** Number of log lines; used to detect when new items are added. */
  lineCount: number;
  /** Called to scroll to the bottom when user clicks "Jump to bottom". */
  scrollToBottom: () => void;
  /** Called when the user clicks "Jump to bottom" (used for analytics). */
  onJumpToBottom?: () => void;
}

export interface UseAutoScrollResult {
  followOutput: (isAtBottom: boolean) => FollowOutputScalarType;
  handleAtBottomStateChange: (atBottom: boolean) => void;
  handleIsScrolling: (scrolling: boolean) => void;
  handleJumpToBottom: () => void;
  handleRangeChanged: () => void;
  handleScrollerRef: (ref: HTMLElement | Window | null) => void;
  showJumpToBottom: boolean;
}

/**
 * Manages auto-scroll behavior and "jump to bottom" button visibility for the
 * log viewer.
 *
 * Jump-to-bottom button visibility rules:
 *
 * 1. Hide the button at the bottom of the log viewer.
 * 2. If distance from the bottom exceeds a threshold *and* the user has
 *    scrolled down enough cumulatively, show the button.
 * 3. If the user scrolls up, reset the above downward scroll progress.
 * 4. If not at the bottom and items are appended (e.g. from polling), show the
 *    buttom if the current distance exceeds a threshold value.
 */
export function useAutoScroll({
  firstItemIndex,
  startsUnpinned = false,
  lineCount,
  onJumpToBottom,
  scrollToBottom,
}: UseAutoScrollArgs): UseAutoScrollResult {
  const downScrollTracker = useDownScrollTracker();

  const scrollerRef = useRef<HTMLElement | null>(null); // Virtuoso's 'scrollerRef'.
  const handleScrollerRef = useCallback((ref: HTMLElement | Window | null) => {
    scrollerRef.current = ref && ref !== window ? (ref as HTMLElement) : null;
  }, []);

  const isUserScrolling = useRef(false);
  const handleIsScrolling = useCallback((scrolling: boolean) => {
    isUserScrolling.current = scrolling;
  }, []);

  const prevLineCount = usePrevious(lineCount)!;
  const prevFirstItemIndex = usePrevious(firstItemIndex)!;

  const hasNewItems = lineCount > prevLineCount;
  const wasPrepend = firstItemIndex < prevFirstItemIndex;
  const wasAppend = hasNewItems && !wasPrepend;

  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  // Rule 1.
  const isPinnedToBottomRef = useRef(true);
  const hasInitializedPinnedRef = useRef(false);

  // Start unpinned if 'startsUnpinned' is true so that the mount position
  // (restored snapshot or seeded search match) isn't overridden
  // ('atBottomStateChange' will re-pin if needed).
  //
  // NOTE: 'startsUnpinned' may not be known on the first render (e.g. while
  // data is loading), so defer until some logs are present.
  // oxlint-disable-next-line react/refs
  if (!hasInitializedPinnedRef.current && lineCount > 0) {
    // oxlint-disable-next-line react/refs
    isPinnedToBottomRef.current = !startsUnpinned;
    hasInitializedPinnedRef.current = true;
  }

  const handleAtBottomStateChange = useCallback(
    (atBottom: boolean) => {
      if (atBottom) {
        isPinnedToBottomRef.current = true;
        setShowJumpToBottom(false);
        downScrollTracker.reset();
      }
    },
    [downScrollTracker]
  );

  const lastChangeWasPrepend = useRef(false);
  const scrollTopPrevRef = useRef(0);

  // TODO: Virtuoso's 'rangeChanged' handler only fires when the visible item
  // range changes. This provides a natural ~1 line-height deadzone that filters
  // out small scroll adjustments. This is useful, but consider formalizing this
  // instead of relying on this behavior implicitly.
  const handleRangeChanged = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    const {scrollTop, scrollHeight, clientHeight} = scroller;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    const scrollTopPrev = scrollTopPrevRef.current;
    scrollTopPrevRef.current = scrollTop;

    const scrolledUp = scrollTop < scrollTopPrev;
    const scrolledDown = scrollTop > scrollTopPrev;

    if (scrolledUp) {
      // Rule 3.
      isPinnedToBottomRef.current = false;
      setShowJumpToBottom(false);
      downScrollTracker.reset();
      lastChangeWasPrepend.current = false;
    } else if (scrolledDown) {
      // Ignore the next downward scroll if items were just prepended, as this
      // is likely a scroll adjustment by Virtuoso. It's possibly a user scroll,
      // but that's okay because scroll contributions are capped; the adjustment
      // alone won't trigger the button showing.
      if (lastChangeWasPrepend.current) {
        lastChangeWasPrepend.current = false;
        return;
      }

      downScrollTracker.addDownwardScrollDelta(scrollTop - scrollTopPrev);
      const scrolledDownDistance = downScrollTracker.getAccumulated();

      // Rule 2.
      if (
        distanceFromBottom > JUMP_TO_BOTTOM_THRESHOLD_PX &&
        scrolledDownDistance >= JUMP_TO_BOTTOM_DISTANCE_SHOW_PX
      ) {
        setShowJumpToBottom(true);
      }
    }
  }, [downScrollTracker]);

  // Rule 4.
  useEffect(() => {
    // Mark that a prepend occurred so 'handleRangeChanged' can ignore
    // Virtuoso's incoming scroll adjustment.
    if (hasNewItems && wasPrepend) {
      lastChangeWasPrepend.current = true;
    }

    const scroller = scrollerRef.current;
    if (
      wasAppend &&
      !isPinnedToBottomRef.current &&
      !isUserScrolling.current &&
      scroller
    ) {
      const distanceFromBottom =
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (distanceFromBottom > JUMP_TO_BOTTOM_THRESHOLD_PX) {
        setShowJumpToBottom(true);
      }
    }
  }, [firstItemIndex, lineCount, hasNewItems, wasPrepend, wasAppend]);

  const handleJumpToBottom = useCallback(() => {
    setShowJumpToBottom(false);
    downScrollTracker.reset();
    scrollToBottom();
    onJumpToBottom?.();
  }, [downScrollTracker, onJumpToBottom, scrollToBottom]);

  const followOutput = useCallback(
    (_: boolean): FollowOutputScalarType =>
      isPinnedToBottomRef.current ? 'auto' : false,
    []
  );

  return {
    followOutput,
    handleAtBottomStateChange,
    handleIsScrolling,
    handleJumpToBottom,
    handleRangeChanged,
    handleScrollerRef,
    showJumpToBottom,
  };
}

function useDownScrollTracker() {
  const scrollAmountRef = useRef(0);

  const reset = () => {
    scrollAmountRef.current = 0;
  };

  const addDownwardScrollDelta = (delta: number) => {
    const amount = Math.min(delta, JUMP_TO_BOTTOM_MAX_SINGLE_SCROLL_PX);
    scrollAmountRef.current += amount;
  };

  const getAccumulated = useCallback(() => scrollAmountRef.current, []);

  return {addDownwardScrollDelta, getAccumulated, reset};
}
